import React, { useState, useEffect } from 'react';
import { FileText, ChevronRight, Loader2, Zap } from 'lucide-react';

const BASE = process.env.REACT_APP_API_URL || '';

export default function IssueTemplateSelector({ onSelect, onClose }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await fetch(`${BASE}/service/templates`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setTemplates(data.templates || []);
        }
      } catch (err) {
        console.error('Failed to load templates:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  function handleSelect(template) {
    onSelect({
      call_reason: template.reason,
      call_reason_detail: template.reason_detail_template || '',
      follow_up_days: template.follow_up_days || null,
      templateName: template.name,
    });
    if (onClose) onClose();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 size={18} className="text-slate-400 animate-spin" />
      </div>
    );
  }

  if (templates.length === 0) return null;

  // Group by category
  const grouped = templates.reduce((acc, t) => {
    const cat = t.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 px-1">
        <Zap size={14} className="text-amber-500" />
        <span className="text-xs font-bold text-slate-600">Quick Templates</span>
      </div>

      {Object.entries(grouped).map(([category, items]) => (
        <div key={category}>
          <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider px-1 mb-1.5">
            {category}
          </div>
          <div className="space-y-1">
            {items.map(template => (
              <button
                key={template.id}
                onClick={() => handleSelect(template)}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg border border-slate-100 hover:border-indigo-200 hover:bg-indigo-50/50 transition-colors text-left group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={14} className="text-slate-400 group-hover:text-indigo-500 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-slate-700 truncate">{template.name}</div>
                    {template.reason_detail_template && (
                      <div className="text-[10px] text-slate-400 truncate">{template.reason_detail_template}</div>
                    )}
                  </div>
                </div>
                <ChevronRight size={14} className="text-slate-300 group-hover:text-indigo-400 flex-shrink-0" />
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
